import { useFlags } from 'launchdarkly-react-client-sdk';
import { Dispatch, SetStateAction, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { BooleanOptionsValue, CepStatus } from 'basics/enums/maya.enums';
import booleanOptions from 'basics/options/boolean.options';
import { SelectOption } from 'basics/options/options.types';
import channelAffinityFilterValueOptions from 'basics/options/targetsAffinity.options';
import useTargetsFiltersOptions from 'basics/options/targetsFilters.options';
import { CepStateType } from 'basics/types/maya.types';
import useDynamicTargetListSchema from 'components/DynamicTargetListForm/DynamicTargetListForm.schema';
import { DynamicTargetListFormConfigType } from 'components/DynamicTargetListForm/DynamicTargetListForm.types';
import { TargetFilter, TargetsFacets } from 'generated/maya.types';
import cepState from 'states/cep.states';

const useDynamicTargetListConfig = (
  targetsFacets: TargetsFacets,
  filteredTargetsFacets: TargetsFacets,
  targetFilters: TargetFilter,
  setCountry: Dispatch<SetStateAction<string | null>>,
  setBusinessUnit: Dispatch<SetStateAction<string | null>>,
) => {
  const { t } = useTranslation();

  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const isCepApproved = cepStateValue.cep?.status === CepStatus.APPROVED;

  const dynamicTargetListSchema = useDynamicTargetListSchema();
  const targetFiltersOptions = useTargetsFiltersOptions(targetsFacets);
  const filteredTargetsFacetsOption = useTargetsFiltersOptions(filteredTargetsFacets);
  const flags = useFlags();

  const shouldBeDisabled = isCepApproved
    && (!flags.mayaBdcfcsd653ExtendEditingOfApprovedCep || !flags.mayaBdcfcsd1251EnableEditingOfTargetListOfApprovedCep);

  const dynamicTargetListConfig: DynamicTargetListFormConfigType = {
    country: {
      label: t('maya_dynamic_target_list_form_country_label'),
      name: 'country',
      placeholder: t('maya_dynamic_target_list_form_country_placeholder'),
      required: true,
      options: targetFiltersOptions.countries || [],
      defaultValue: targetFilters?.countries ? targetFilters?.countries[0] : null,
      validation: dynamicTargetListSchema.country,
      onChange: ((_: SyntheticEvent, value: SelectOption) => setCountry(value.id as string)),
      disabled: true,
    },
    type: {
      label: t('maya_dynamic_target_list_form_type_label'),
      name: 'type',
      placeholder: t('maya_dynamic_target_list_form_type_placeholder'),
      options: filteredTargetsFacetsOption.types || [],
      required: false,
      defaultValue: targetFilters?.types ? targetFilters?.types : [],
      disabled: shouldBeDisabled || !filteredTargetsFacetsOption.types?.length,
    },
    businessUnit: {
      label: t('maya_dynamic_target_list_form_businessUnit_label'),
      name: 'businessUnit',
      placeholder: t('maya_dynamic_target_list_form_businessUnit_placeholder'),
      options: targetFiltersOptions.businessUnits || [],
      required: true,
      defaultValue: targetFilters?.businessUnits ? targetFilters?.businessUnits[0] : null,
      validation: dynamicTargetListSchema.businessUnit,
      onChange: ((_: SyntheticEvent, value: SelectOption) => setBusinessUnit(value.id as string)),
      disabled: shouldBeDisabled,
    },
    specialty: {
      label: t('maya_dynamic_target_list_form_specialty_label'),
      name: 'specialty',
      placeholder: t('maya_dynamic_target_list_form_specialty_placeholder'),
      options: filteredTargetsFacetsOption.specialties || [],
      required: false,
      defaultValue: targetFilters?.specialties ? targetFilters?.specialties : [],
      disabled: shouldBeDisabled || !filteredTargetsFacetsOption.specialties?.length,
    },
    smaSegments: {
      label: t('maya_dynamic_target_list_form_smaSegments_label'),
      name: 'smaSegments',
      placeholder: t('maya_dynamic_target_list_form_smaSegments_placeholder'),
      options: filteredTargetsFacetsOption.smaSegments || [],
      required: false,
      defaultValue: targetFilters?.smaSegments || [],
      disabled: shouldBeDisabled || !filteredTargetsFacetsOption.smaSegments?.length,
    },
    biosSegments: {
      label: t('maya_dynamic_target_list_form_biosSegments_label'),
      name: 'biosSegments',
      placeholder: t('maya_dynamic_target_list_form_biosSegments_placeholder'),
      options: filteredTargetsFacetsOption.biosSegments || [],
      required: false,
      defaultValue: targetFilters?.biosSegments || [],
      disabled: shouldBeDisabled || !filteredTargetsFacetsOption.biosSegments?.length,
    },
    imsSegments: {
      label: t('maya_dynamic_target_list_form_imsSegments_label'),
      name: 'imsSegments',
      placeholder: t('maya_dynamic_target_list_form_imsSegments_placeholder'),
      options: filteredTargetsFacetsOption.imsSegments || [],
      required: false,
      defaultValue: targetFilters?.imsSegments || [],
      disabled: shouldBeDisabled || !filteredTargetsFacetsOption.imsSegments?.length,
    },
    approvedEmailsAffinity: {
      label: t('maya_dynamic_target_list_form_approvedEmailsAffinity_label'),
      name: 'approvedEmailsAffinity',
      placeholder: t('maya_dynamic_target_list_form_approvedEmaislAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.approvedEmails ? targetFilters?.channelAffinity.approvedEmails : undefined,
      disabled: shouldBeDisabled,
    },
    broadcastEmailsAffinity: {
      label: t('maya_dynamic_target_list_form_broadcastEmailsAffinity_label'),
      name: 'broadcastEmailsAffinity',
      placeholder: t('maya_dynamic_target_list_form_broadcastEmailsAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.broadcastEmails ? targetFilters?.channelAffinity.broadcastEmails : undefined,
      disabled: shouldBeDisabled,
    },
    f2fAffinity: {
      label: t('maya_dynamic_target_list_form_f2fAffinity_label'),
      name: 'f2fAffinity',
      placeholder: t('maya_dynamic_target_list_form_f2fAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.f2f ? targetFilters?.channelAffinity.f2f : undefined,
      disabled: shouldBeDisabled,
    },
    f2fEventAffinity: {
      label: t('maya_dynamic_target_list_form_f2fEventAffinity_label'),
      name: 'f2fEventAffinity',
      placeholder: t('maya_dynamic_target_list_form_f2fEventAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.f2fEvent ? targetFilters?.channelAffinity.f2fEvent : undefined,
      disabled: shouldBeDisabled,
    },
    phoneAffinity: {
      label: t('maya_dynamic_target_list_form_phoneAffinity_label'),
      name: 'phoneAffinity',
      placeholder: t('maya_dynamic_target_list_form_phoneAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.phone ? targetFilters?.channelAffinity.phone : undefined,
      disabled: shouldBeDisabled,
    },
    virtualAffinity: {
      label: t('maya_dynamic_target_list_form_virtualAffinity_label'),
      name: 'virtualAffinity',
      placeholder: t('maya_dynamic_target_list_form_virtualAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.virtual ? targetFilters?.channelAffinity.virtual : undefined,
      disabled: shouldBeDisabled,
    },
    virtualEventAffinity: {
      label: t('maya_dynamic_target_list_form_virtualEventAffinity_label'),
      name: 'virtualEventAffinity',
      placeholder: t('maya_dynamic_target_list_form_virtualEventAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.virtualEvent ? targetFilters?.channelAffinity.virtualEvent : undefined,
      disabled: shouldBeDisabled,
    },
    webSessionsAffinity: {
      label: t('maya_dynamic_target_list_form_webSessionsAffinity_label'),
      name: 'webSessionsAffinity',
      placeholder: t('maya_dynamic_target_list_form_webSessionsAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.webSessions ? targetFilters?.channelAffinity.webSessions : undefined,
      disabled: shouldBeDisabled,
    },
    allF2FAffinity: {
      label: t('maya_dynamic_target_list_form_allF2FAffinity_label'),
      name: 'allF2FAffinity',
      placeholder: t('maya_dynamic_target_list_form_allF2FAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.allF2F ? targetFilters?.channelAffinity.allF2F : undefined,
      disabled: shouldBeDisabled,
    },
    allDigitalAffinity: {
      label: t('maya_dynamic_target_list_form_allDigitalAffinity_label'),
      name: 'allDigitalAffinity',
      placeholder: t('maya_dynamic_target_list_form_allDigitalAffinity_placeholder'),
      required: false,
      options: channelAffinityFilterValueOptions,
      defaultValue: targetFilters?.channelAffinity?.allDigital ? targetFilters?.channelAffinity.allDigital : undefined,
      disabled: shouldBeDisabled,
    },
    hcoNames: {
      label: t('maya_dynamic_target_list_form_hcoNames_label'),
      name: 'hcoNames',
      placeholder: t('maya_dynamic_target_list_form_hcoNames_placeholder'),
      required: false,
      options: targetFiltersOptions.hcoNames || [],
      defaultValue: targetFilters?.hcoNames?.length ? targetFilters?.hcoNames : [],
      disabled: shouldBeDisabled,
    },
    consent: {
      label: t('maya_dynamic_target_list_form_consent_label'),
      name: 'consent',
      placeholder: t('maya_dynamic_target_list_form_consent_placeholder'),
      required: false,
      options: targetFiltersOptions.consent || [],
      defaultValue: targetFilters?.consent?.length ? targetFilters?.consent : [],
      disabled: shouldBeDisabled,
    },
    cyclePlan: {
      label: t('maya_dynamic_target_list_form_cyclePlan_label'),
      name: 'cyclePlan',
      placeholder: t('maya_dynamic_target_list_form_cyclePlan_placeholder'),
      required: false,
      options: booleanOptions,
      defaultValue: BooleanOptionsValue[targetFilters?.cyclePlan?.toString() as keyof typeof BooleanOptionsValue] || null,
      disabled: shouldBeDisabled,
    },
  };

  return {
    dynamicTargetListConfig,
  };
};

export default useDynamicTargetListConfig;
